import { useCallback, useMemo, useRef, useEffect } from 'react';

import PropTypes from 'prop-types';

import Alert from '@zenbusiness/zui/Alert';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { FIELD_REGISTRY } from './FieldTypes/FieldRegistry';
import * as Styled from './FormStep.style';
import { isPageValid, getFieldErrorMessage } from './validation';
import { getVisibleFields } from './visibility';
import { useCustomForm } from '../../contexts/CustomFormContext';

/**
 * Renders a single form step with multiple fields in a configurable layout.
 * Handles field rendering, validation, and form submission.
 * Now integrates with CustomFormContext for centralized validation.
 *
 * @param {object} props - Component props
 * @param {object} props.page - Form step page configuration
 * @param {object} props.answers - Current form answers (from CustomFormContext)
 * @param {Function} props.onFieldChange - Field change handler
 * @param {Function} props.onBack - Back button handler
 * @param {Function} props.onContinue - Continue button handler
 * @param {Function} props.isFieldVisible - Field visibility checker
 * @param {boolean} [props.canGoBack=false] - Whether back button should be shown
 * @param {boolean} [props.isLoading=false] - Whether form is in loading state
 * @param {string} [props.error] - Global error message
 */
function FormStep({
  page,
  answers,
  onFieldChange,
  onBack,
  onContinue,
  isFieldVisible,
  canGoBack = false,
  isLoading = false,
  error
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const firstErrorFieldRef = useRef(null);

  // Use CustomFormContext for validation
  // const { errors: contextErrors, validateField: contextValidateField, getError } = useCustomForm();
  const { getError } = useCustomForm();

  // Get visible fields
  const visibleFields = useMemo(
    () => getVisibleFields(page.fields || [], answers),
    [page.fields, answers]
  );

  // Auto-continue is disabled - always use Continue button
  const autoContinue = {
    isAutoContinuing: false,
    announcementText: '',
    shouldShowContinueButton: true,
    shouldDisableForm: false
  };

  // Calculate field errors for display (excludes required field errors)
  const fieldErrors = useMemo(() => {
    const errors = {};
    visibleFields.forEach((field) => {
      // First try to get error from CustomFormContext
      const contextError = getError(field.id);
      if (contextError) {
        errors[field.id] = contextError;
      } else {
        const fieldError = getFieldErrorMessage(field, answers[field.id]);
        if (fieldError) {
          errors[field.id] = fieldError;
        }
      }
    });
    return errors;
    // }, [visibleFields, answers, getError]);
  }, [visibleFields, answers, getError]);

  // Check if form can be submitted
  const canContinue = useMemo(
    () => isPageValid(page.fields || [], answers, isFieldVisible),
    [page.fields, answers, isFieldVisible]
  );

  // Combine form loading state with auto-continue state
  const isFormDisabled = isLoading || autoContinue.shouldDisableForm;

  // Focus first error field when validation fails
  useEffect(() => {
    if (Object.keys(fieldErrors).length > 0 && firstErrorFieldRef.current) {
      firstErrorFieldRef.current.focus();
    }
  }, [fieldErrors]);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!canContinue || isFormDisabled) {
        return;
      }
      onContinue();
    },
    [canContinue, isFormDisabled, onContinue]
  );

  return (
    <Styled.FormStepRoot>
      <form onSubmit={handleSubmit}>
        <fieldset disabled={isFormDisabled}>
          <legend className="sr-only">{page.title || 'Form Step'}</legend>

          {page.title && <Styled.StepTitle>{page.title}</Styled.StepTitle>}

          {error && (
            <Alert severity="error" style={{ marginBottom: '16px' }}>
              {error}
            </Alert>
          )}

          <Styled.FieldsContainer
            columns={page.layout?.columns || 1}
            gap={page.layout?.gap || 'md'}
          >
            {visibleFields.map((field, index) => {
              const FieldComponent = FIELD_REGISTRY[field.type];
              const fieldError = fieldErrors[field.id];
              const isFirstError = index === 0 && fieldError;

              if (!FieldComponent) {
                return (
                  <Styled.FieldError key={field.id}>
                    Unknown field type: {field.type}
                  </Styled.FieldError>
                );
              }

              return (
                <Box
                  key={field.id}
                  ref={isFirstError ? firstErrorFieldRef : null}
                  data-error={Boolean(fieldError)}
                >
                  <FieldComponent
                    def={field}
                    disabled={isFormDisabled}
                    error={fieldError}
                    value={answers[field.id]}
                    onChange={(value) => onFieldChange(field.id, value)}
                  />
                </Box>
              );
            })}
          </Styled.FieldsContainer>

          <Styled.ActionsContainer>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                {canGoBack && (
                  <Button
                    disabled={isFormDisabled}
                    fullWidth={isMobile}
                    variant="outlined"
                    onClick={onBack}
                  >
                    Back
                  </Button>
                )}
              </Box>

              <Button
                color="primary"
                disabled={!canContinue || isFormDisabled}
                fullWidth={isMobile}
                loading={isFormDisabled}
                type="submit"
                variant="contained"
              >
                Next
              </Button>
            </Stack>
          </Styled.ActionsContainer>
        </fieldset>
      </form>
    </Styled.FormStepRoot>
  );
}

FormStep.propTypes = {
  page: PropTypes.shape({
    id: PropTypes.string.isRequired,
    kind: PropTypes.oneOf(['form_step']).isRequired,
    title: PropTypes.string,
    layout: PropTypes.shape({
      columns: PropTypes.oneOf([1, 2, 3]),
      gap: PropTypes.oneOf(['sm', 'md', 'lg'])
    }),
    autoContinue: PropTypes.bool,
    fields: PropTypes.arrayOf(PropTypes.object).isRequired,
    next: PropTypes.object
  }).isRequired,
  answers: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  isFieldVisible: PropTypes.func.isRequired,
  canGoBack: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.string
};

export default FormStep;
